import React, { useEffect, useState } from "react";
import { AiOutlineEdit } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Badge, Col, Container, Row } from "reactstrap";
import { SubmissionError } from "redux-form";

import SignatureModal from "./SignatureModal/signatureModal";
import FinancialStatusModal from "./SignatureModal/financialStatusModal";
import ManagementDataModal, {
	phaseOptions,
	touchOptions
} from "./SignatureModal/managementDataModal";

import { MultiStatusCards } from "components/MultiStatusCards";

import * as api from "services/api";

import { formatCurrencyValue } from "helpers/string";

import {
	onboardPhase,
	onboardPhaseType,
	onboardTouch
} from "types/onboardPhase";

import * as actions from "../actions";

import { FinancialStatusBadge } from "./financialStatusBadge";
// import { PhaseBadge } from "./phaseBadge";

const EMPTY_VALUE = "-";

const PHASE_BADGE_COLORS = {
	[onboardPhaseType.Onboard]: "warning",
	[onboardPhaseType.Ongoing]: "success"
};

const TOUCH_BADGE_COLORS = {
	low: "#28a745",
	mid: "#007bff",
	high: "#6f42c1",
	enterprise: "#fd7e14"
};

const hasValue = value => value !== undefined && value !== null && value !== "";

const toManagementFormValues = onboard => ({
	salerName: onboard?.salerName || "",
	accountManagerName: onboard?.accountManagerName || "",
	step: onboard?.step || "",
	phase:
		phaseOptions.find(option => option.value === String(onboard?.phase)) ||
		null,
	touch:
		touchOptions.find(
			option => option.value === String(onboard?.touch || "").toLowerCase()
		) || null,
	mrr: hasValue(onboard?.mrr) ? onboard.mrr / 100 : null
});

const toManagementPayload = values => ({
	salerName: values.salerName || "",
	accountManagerName: values.accountManagerName || "",
	step: values.step || "",
	phase: Number(values.phase?.value),
	touch: values.touch?.value || "",
	mrr: hasValue(values.mrr) ? Math.round(values.mrr * 100) : null
});

const SignatureDetail = () => {
	const dispatch = useDispatch();

	const { item, dashboard } = useSelector(c => c.onBoardDetail);
	const { item: company } = useSelector(s => s.companyDetail);
	const { token } = useSelector(s => s.login);

	const [openSignaturesModal, setOpenSignaturesModal] = useState(false);
	const [openFinancialStatusModal, setOpenFinancialStatusModal] = useState(
		false
	);
	const [
		initialFinancialStatusValue,
		setInitialFinancialStatusValue
	] = useState(null);
	const [financialStatusToUpdate, setFinancialStatusToUpdate] = useState(0);
	const [openManagementDataModal, setOpenManagementDataModal] = useState(false);

	const saveManagementData = values =>
		new Promise((resolve, reject) => {
			dispatch(
				actions.updateManagementData(
					company._id,
					toManagementPayload(values),
					errors => {
						if (errors) {
							reject(
								new SubmissionError({
									_error: errors.default || "Erro ao salvar os dados de gestão"
								})
							);
							return;
						}

						setOpenManagementDataModal(false);
						toast.success("Dados de gestão salvos com sucesso!");
						dispatch(actions.getOnboardData(company._id, e => console.log(e)));
						resolve();
					}
				)
			);
		});

	const savePurchasedProviders = data => {
		dispatch(
			actions.updatePurchasedProviders(
				{
					_id: item._id,
					purchasedProviders: data.purchasedProviders
				},
				(err, res) => {
					if (err) {
						console.log("error", err);
						return;
					}

					if (res) {
						setOpenSignaturesModal(false);
						dispatch(actions.getOnboardData(company._id, e => console.log(e)));
					}
				}
			)
		);
	};

	async function getInitialFinancialStatus() {
		try {
			const response = await api.sendGet(`/v2/onboard/license/${company._id}`, {
				Authorization: "Bearer " + token
			});

			const result = await response.json();

			if (result.isValid) {
				setInitialFinancialStatusValue(result.model.status);
			}
		} catch (err) {
			console.log(err);
		}
	}

	async function handleFinancialStatusSave(event) {
		event.preventDefault();

		try {
			await api.sendPut(
				`/v2/onboard/change-license-status/${company._id}`,
				{
					status: financialStatusToUpdate
				},
				{
					Authorization: "Bearer " + token
				}
			);
		} catch (err) {
			console.log(err);
		} finally {
			window.location.reload();
		}
	}

	useEffect(() => {
		dispatch(actions.getOnboardData(company._id, e => console.log(e)));
	}, []);

	return (
		<Container className="dashboard">
			<Row
				style={{
					height: "auto",
					marginBottom: "1rem"
				}}
			>
				<Col
					style={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "space-between",
						gap: "0.5rem"
					}}
				>
					<MultiStatusCards
						cardsData={[
							{
								children: [
									{
										title: (
											<div>
												Contratados
												<span
													className="onboard-edit-button"
													onClick={() => setOpenSignaturesModal(true)}
												>
													<AiOutlineEdit />
												</span>
											</div>
										),
										color: "info",
										count: item?.purchasedProviders || 0,
										onClick: () => {}
									}
								]
							},
							{
								children: [
									{
										title: "Ativos",
										color: "success",
										count:
											(dashboard?.enabled || 0) +
											(dashboard?.onboard || 0) +
											(dashboard?.pending || 0),
										onClick: () => {}
									}
								]
							},

							{
								children: [
									{
										title: "Inativos",
										color: "danger",
										count: dashboard?.disabled || 0,
										onClick: () => {}
									}
								]
							}
						]}
					/>
				</Col>
			</Row>
			<div>
				<Row className="mt-3">
					<Col className="d-flex align-items-center">
						<h4 className="onboard-header-edit">
							<strong>Dados de gestão</strong>
							<span
								className="onboard-edit-button"
								onClick={() => setOpenManagementDataModal(true)}
							>
								<AiOutlineEdit />
							</span>
						</h4>
					</Col>
				</Row>
				<Row style={{ flexWrap: "wrap" }}>
					<Col className="mt-3" xs={12} sm={12} md={6} xl={3}>
						<div>
							<strong className="onboard-header-edit">Vendedor:</strong>
							<br />
							<span>{item?.salerName || EMPTY_VALUE}</span>
						</div>
					</Col>
					<Col className="mt-3" xs={12} sm={12} md={6} xl={3}>
						<div>
							<strong className="onboard-header-edit">
								Gerente de Contas:
							</strong>
							<br />
							<span>{item?.accountManagerName || EMPTY_VALUE}</span>
						</div>
					</Col>
					<Col className="mt-3" xs={12} sm={12} md={6} xl={3}>
						<div>
							<strong className="onboard-header-edit">Fase:</strong>
							<br />
							<div
								style={{
									fontSize: "1rem",
									display: "flex",
									flexDirection: "column"
								}}
							>
								{onboardPhase[item?.phase] ? (
									<Badge
										style={{ width: "fit-content", textTransform: "uppercase" }}
										color={PHASE_BADGE_COLORS[item.phase]}
									>
										{onboardPhase[item.phase]}
									</Badge>
								) : (
									<span>{EMPTY_VALUE}</span>
								)}
							</div>
						</div>
					</Col>
					<Col className="mt-3" xs={12} sm={12} md={6} xl={3}>
						<div>
							<strong className="onboard-header-edit">Etapa:</strong>
							<br />
							<div
								style={{
									fontSize: "1rem",
									display: "flex",
									flexDirection: "column"
								}}
							>
								{item?.step ? (
									<Badge
										style={{ width: "fit-content", textTransform: "uppercase" }}
										color="secondary"
									>
										{item.step}
									</Badge>
								) : (
									<span>{EMPTY_VALUE}</span>
								)}
							</div>
						</div>
					</Col>
					<Col className="mt-3" xs={12} sm={12} md={6} xl={3}>
						<div>
							<strong className="onboard-header-edit">Touch:</strong>
							<br />
							<div
								style={{
									fontSize: "1rem",
									display: "flex",
									flexDirection: "column"
								}}
							>
								{item?.touch ? (
									<Badge
										style={{
											width: "fit-content",
											textTransform: "uppercase",
											color: "#fff",
											backgroundColor:
												TOUCH_BADGE_COLORS[item.touch.toLowerCase()]
										}}
										color="secondary"
									>
										{onboardTouch[item.touch.toLowerCase()] || item.touch}
									</Badge>
								) : (
									<span>{EMPTY_VALUE}</span>
								)}
							</div>
						</div>
					</Col>
					<Col className="mt-3" xs={12} sm={12} md={6} xl={3}>
						<div>
							<strong className="onboard-header-edit">MRR:</strong>
							<br />
							<span>
								{hasValue(item?.mrr)
									? formatCurrencyValue(item.mrr)
									: EMPTY_VALUE}
							</span>
						</div>
					</Col>
				</Row>
			</div>

			<div className="topbar__menu-divider" />

			<Row>
				<Col className="mt-3" xs={12} sm={12} md={6} xl={3}>
					<div>
						<strong className="onboard-header-edit">
							Status Financeiro:
							<div
								className="onboard-edit-button"
								onClick={() => setOpenFinancialStatusModal(true)}
							>
								<AiOutlineEdit />
							</div>
						</strong>
						<br />
						<div
							style={{
								fontSize: "1rem",
								display: "flex",
								flexDirection: "column"
							}}
						>
							{!(initialFinancialStatusValue === null)
								? FinancialStatusBadge(initialFinancialStatusValue)
								: null}
						</div>
					</div>
					<FinancialStatusModal
						toggle={() => setOpenFinancialStatusModal(false)}
						modalOpen={openFinancialStatusModal}
						defaultValue={initialFinancialStatusValue}
						handleSubmit={handleFinancialStatusSave}
						handleChange={setFinancialStatusToUpdate}
					/>
				</Col>
			</Row>

			<SignatureModal
				toggle={() => setOpenSignaturesModal(false)}
				modalOpen={openSignaturesModal}
				onSubmit={savePurchasedProviders}
				initialValues={{ purchasedProviders: item?.purchasedProviders || 0 }}
			/>

			<ManagementDataModal
				toggle={() => setOpenManagementDataModal(false)}
				modalOpen={openManagementDataModal}
				onSubmit={saveManagementData}
				initialValues={toManagementFormValues(item)}
			/>
		</Container>
	);
};

export default SignatureDetail;
